import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/apiConfig';

// MathVenture Registration Component - Clean Version

const Register = ({ setUser }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    const API_BASE = getApiBaseUrl();
    console.log('Using API Base URL:', API_BASE);

    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password
        })
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Store user data in localStorage for persistence
        localStorage.setItem('user', JSON.stringify(userData));
        
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(`Network error: ${error.message}. Make sure the backend server is running on ${API_BASE}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-200 via-blue-200 to-purple-200 flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Fun floating math symbols */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 text-2xl text-yellow-500 opacity-70 animate-bounce">➕</div>
        <div className="absolute top-20 right-20 text-xl text-green-500 opacity-70 animate-pulse">✖️</div>
        <div className="absolute bottom-20 left-20 text-3xl text-orange-500 opacity-70 animate-bounce" style={{animationDelay: '1s'}}>➖</div>
        <div className="absolute bottom-10 right-10 text-2xl text-red-500 opacity-70 animate-pulse" style={{animationDelay: '0.5s'}}>➗</div>
        <div className="absolute top-1/2 left-5 text-xl text-blue-500 opacity-70 animate-bounce" style={{animationDelay: '2s'}}>🔢</div>
        <div className="absolute top-1/3 right-5 text-2xl text-purple-500 opacity-70 animate-pulse" style={{animationDelay: '1.5s'}}>📐</div>
        <div className="absolute top-1/4 left-1/4 text-lg text-pink-500 opacity-70 animate-bounce" style={{animationDelay: '3s'}}>🧮</div>
        <div className="absolute bottom-1/3 right-1/3 text-xl text-indigo-500 opacity-70 animate-pulse" style={{animationDelay: '2.5s'}}>📊</div>
      </div>
      
      <div className="max-w-sm w-full space-y-4 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-6 border-3 border-green-400 transform hover:scale-105 transition-transform duration-300">
          <div className="text-center">
            <div className="mx-auto flex justify-center mb-4 relative">
              <div className="relative">
                <img 
                  src="/images/logo/MathVenture_logo.png" 
                  alt="MathVenture Logo" 
                  className="h-16 w-auto animate-pulse"
                />
                <div className="absolute -top-1 -right-1 text-lg animate-bounce">🌟</div>
                <div className="absolute -bottom-1 -left-1 text-lg animate-bounce" style={{animationDelay: '0.5s'}}>🎯</div>
              </div>
            </div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              🎊 Join the Adventure! 🎊
            </h2>
            <p className="text-sm text-green-700 font-medium mb-2">
              Create your account and start exploring!
            </p>
            <p className="text-xs text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-bold text-blue-600 hover:text-blue-700 underline decoration-wavy">
                Sign in here!
              </Link>
            </p>
          </div>
          
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-3">
              <div className="relative">
                <label htmlFor="username" className="block text-xs font-bold text-green-700 mb-1">
                  🎭 Choose Your Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="w-full px-3 py-2 border-2 border-green-300 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-500 transition-all duration-300 hover:border-green-400 bg-green-50"
                  placeholder="SuperMathHero123"
                  value={formData.username}
                  onChange={handleChange}
                />
                <div className="absolute right-2 top-6 text-sm">🦸</div>
              </div>

              <div className="relative">
                <label htmlFor="email" className="block text-xs font-bold text-blue-700 mb-1">
                  📧 Your Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-3 py-2 border-2 border-blue-300 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition-all duration-300 hover:border-blue-400 bg-blue-50"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
                <div className="absolute right-2 top-6 text-sm">📮</div>
              </div>

              <div className="relative">
                <label htmlFor="password" className="block text-xs font-bold text-purple-700 mb-1">
                  🔐 Create Secret Password
                </label>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  className="w-full px-3 py-2 pr-10 border-2 border-purple-300 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-500 transition-all duration-300 hover:border-purple-400 bg-purple-50"
                  placeholder="Make it super strong!"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-6 text-purple-600 hover:text-purple-800 focus:outline-none"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
                <div className="absolute right-2 top-6 text-sm"></div>
              </div>

              <div className="relative">
                <label htmlFor="confirmPassword" className="block text-xs font-bold text-pink-700 mb-1">
                  🔒 Confirm Your Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  className="w-full px-3 py-2 pr-10 border-2 border-pink-300 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-500 transition-all duration-300 hover:border-pink-400 bg-pink-50"
                  placeholder="Type it again to be sure!"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-6 text-pink-600 hover:text-pink-800 focus:outline-none"
                  title={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? "🙈" : "👁️"}
                </button>
                <div className="absolute right-2 top-6 text-sm"></div>
              </div>
            </div>

            {error && (
              <div className="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-2 rounded-xl text-xs font-medium flex items-center">
                <span className="mr-2 text-sm">⚠️</span>
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border-0 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span className="mr-2 text-sm">🎉</span>
                {loading ? 'Creating Account... ⏳' : 'Start My Adventure! 🚀'}
                <span className="ml-2 text-sm">🌟</span>
              </button>
            </div>

            <div className="text-center bg-gradient-to-r from-yellow-100 to-orange-100 rounded-xl p-3 border-2 border-yellow-300">
              <p className="text-xs text-gray-700 font-medium">
                <span className="text-sm mr-1">🎁</span>
                Ready to become a Math Champion?
                <span className="text-sm ml-1">🏆</span>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;